import React, { useContext, useLayoutEffect, useState, useTransition } from 'react';
import { Router } from 'react-router';

// The app's router (PANEL-1549): react-router 8's low-level <Router> driven by a history object the
// app owns (src/utils/history.js; specs pass a memory history). react-router's own BrowserRouter and
// HistoryRouter subscribe to history in a layout effect, which runs after their children's
// componentDidMount, so a navigation a page makes while mounting (GDPR and Help push on mount) would
// change the URL and leave the screen behind. react-router 5's Router subscribed in its constructor
// and kept such a location; this does the same by reading history.location once it has subscribed.
// The history must notify on push/replace (v5Compat: true).
// A navigation renders as a transition, as react-router's own routers do (PANEL-1539): the first visit
// to a lazy page suspends, and outside a transition React swaps the screen for the Suspense fallback and
// hides the mounted tree, re-running its class components' componentWillUnmount/componentDidMount when
// it shows again (the Header re-sent its mount-time requests on every such visit). In a transition the
// current screen stays up until the page's code has loaded; useNavigationPending() is true meanwhile, so
// the app can show its loader over it (the URL has already changed).
const NavigationPending = React.createContext(false);
export const useNavigationPending = () => useContext(NavigationPending);

export default function AppRouter({ history, children }) {
  const [state, setState] = useState(() => ({ action: history.action, location: history.location }));
  const [pending, startNavigation] = useTransition();

  useLayoutEffect(() => {
    const unlisten = history.listen(({ action, location }) => startNavigation(() => setState({ action, location })));
    setState((current) =>
      current.location.key === history.location.key ? current : { action: history.action, location: history.location }
    );
    return unlisten;
  }, [history]);

  return (
    <NavigationPending.Provider value={pending}>
      <Router location={state.location} navigationType={state.action} navigator={history}>
        {children}
      </Router>
    </NavigationPending.Provider>
  );
}
